import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Steps,
  Alert,
  Typography,
  Tag,
  Table,
  Button,
  Space,
  Form,
  Select,
  InputNumber,
  Input,
  Checkbox,
  Divider,
  Modal,
  message,
  Statistic,
  Descriptions,
} from 'antd';
import {
  InfoCircleOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SalesOrder, ReturnOrder, ReturnOrderItem, ReturnReason, ReturnCondition } from '../../types';
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ReturnOrderProcessingProps {
  order: SalesOrder;
  onStatusUpdate: (status: string, notes?: string, extra?: { returnReason?: string; handlingMethod?: string }) => void;
  onComplete: () => void;
  mode?: 'process' | 'detail';
  initialValues?: {
    returnReason?: string;
    handlingMethod?: string;
    merchantConfirmation?: string;
  };
  overrideReturnItems?: Array<{
    id?: string;
    originalOrderItemId?: string;
    productId?: string;
    product: any;
    originalQuantity: number;
    returnQuantity: number;
    unitPrice: number;
    returnReason?: any;
    condition?: any;
    restockable?: boolean;
    notes?: string;
  }>;
}

interface ProcessingItem extends ReturnOrderItem {
  selected: boolean;
  maxReturnQuantity: number;
}

const ReturnOrderProcessing: React.FC<ReturnOrderProcessingProps> = ({
  order,
  onStatusUpdate,
  onComplete,
  mode = 'process',
  initialValues,
  overrideReturnItems,
}) => {
    const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [form] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [returnItems, setReturnItems] = useState<ProcessingItem[]>([]);
  const isReadOnly = mode === 'detail';

  // 退貨原因選項
  const returnReasons: { value: ReturnReason; label: string; description: string }[] = [
    { value: 'damaged_in_shipping', label: t('運送損壞'), description: t('商品在運送過程中損壞') },
    { value: 'defective_product', label: t('產品缺陷'), description: t('商品本身存在質量問題') },
    { value: 'wrong_item_sent', label: t('發錯商品'), description: t('發送了錯誤的商品') },
    { value: 'not_as_described', label: t('與描述不符'), description: t('商品與描述不一致') },
    { value: 'changed_mind', label: t('改變主意'), description: t('客戶改變購買決定') },
    { value: 'duplicate_order', label: t('重複訂單'), description: t('客戶不小心下了重複訂單') },
    { value: 'late_delivery', label: t('延遲送達'), description: t('商品送達時間過晚') },
    { value: 'other', label: t('其他原因'), description: t('其他未列出的原因') },
  ];

  const conditions: { value: ReturnCondition; label: string; color: string }[] = [
    { value: 'new', label: t('全新'), color: 'green' },
    { value: 'like_new', label: t('近似全新'), color: 'blue' },
    { value: 'damaged', label: t('損壞'), color: 'orange' },
    { value: 'defective', label: t('有缺陷'), color: 'red' },
    { value: 'unusable', label: t('無法使用'), color: 'red' },
  ];

  const getConditionMeta = (value: any): { label: string; color?: string } => {
    if (!value) return { label: '-' };
    const byValue = conditions.find(c => c.value === value);
    if (byValue) return { label: byValue.label, color: byValue.color };
    const byLabel = conditions.find(c => c.label === value);
    if (byLabel) return { label: byLabel.label, color: byLabel.color };

    // 同義字/英文代碼兼容
    const normalized = String(value).toLowerCase().trim().replace(/\s+/g, '_');
    const alias: Record<string, { label: string; color?: string }> = {
      brand_new: { label: t('全新'), color: 'green' },
      new: { label: t('全新'), color: 'green' },
      like_new: { label: t('近似全新'), color: 'blue' },
      nearly_new: { label: t('近似全新'), color: 'blue' },
      near_new: { label: t('近似全新'), color: 'blue' },
      used: { label: t('已使用'), color: 'gold' },
      damaged: { label: t('損壞'), color: 'orange' },
      broken: { label: t('損壞'), color: 'orange' },
      defective: { label: t('有缺陷'), color: 'red' },
      faulty: { label: t('有缺陷'), color: 'red' },
      unusable: { label: t('無法使用'), color: 'red' },
      not_working: { label: t('無法使用'), color: 'red' },
    };
    if (alias[normalized]) return alias[normalized];
    return { label: String(value) };
  };

  const handlingMethods = [
    { value: 'refund', label: t('退款'), description: t('退還原支付金額') },
    { value: 'exchange', label: t('換貨'), description: t('更換相同或類似商品') },
    { value: 'store_credit', label: t('商店積分'), description: t('轉為商店積分') },
    { value: 'repair', label: t('維修'), description: t('維修後重新發送') },
  ];

  useEffect(() => {
    // 初始化退貨商品數據
    if (overrideReturnItems && overrideReturnItems.length > 0) {
      const items: ProcessingItem[] = overrideReturnItems.map((item, idx) => ({
        id: item.id || String(idx),
        originalOrderItemId: item.originalOrderItemId || '',
        productId: item.productId || item.product?.id,
        product: item.product,
        originalQuantity: item.originalQuantity,
        returnQuantity: item.returnQuantity,
        unitPrice: item.unitPrice,
        returnReason: (item.returnReason as any) || ('damaged_in_shipping' as ReturnReason),
        condition: (item.condition as any) || ('damaged' as ReturnCondition),
        restockable: Boolean(item.restockable),
        selected: true,
        maxReturnQuantity: item.originalQuantity,
        notes: item.notes || '',
      }));
      setReturnItems(items);
    } else if (order && (order as any).items) {
      const items: ProcessingItem[] = (order as any).items.map((item: any) => ({
        id: '',
        originalOrderItemId: item.id,
        productId: item.productId,
        product: item.product,
        originalQuantity: item.quantity,
        returnQuantity: 1, // 默認退貨數量為1
        unitPrice: item.unitPrice,
        returnReason: 'damaged_in_shipping' as ReturnReason,
        condition: 'damaged' as ReturnCondition,
        restockable: false,
        selected: item.product?.name?.includes('Bluetooth Speaker') || false,
        maxReturnQuantity: item.deliveredQuantity || item.quantity,
        notes: '',
      }));
      setReturnItems(items);
    }

    // 設置表單默認值
    form.setFieldsValue({
      returnReason: initialValues?.returnReason ?? '其中一個蓝牙音箱在运輸過程中损坏，客戶要求退貨處理。',
      handlingMethod: initialValues?.handlingMethod ?? 'refund',
      merchantConfirmation: initialValues?.merchantConfirmation ?? '商家確認：同意退款，损坏商品将安排退回倉庫處理。',
    });

    if (isReadOnly) {
      setCurrentStep(0);
    }
  }, [order, form, isReadOnly, initialValues, overrideReturnItems]);

  const handleItemChange = (index: number, field: keyof ProcessingItem, value: any) => {
    const newItems = [...returnItems];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // 自動設置可補貨狀態
    if (field === 'condition') {
      newItems[index].restockable = value === 'new' || value === 'like_new';
    }
    
    setReturnItems(newItems);
  };

  const getSelectedItems = () => {
    return returnItems.filter(item => item.selected && item.returnQuantity > 0);
  };

  const calculateTotalRefund = () => {
    return getSelectedItems().reduce((total, item) => {
      return total + (item.returnQuantity * item.unitPrice);
    }, 0);
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      const selectedItems = getSelectedItems();
      if (selectedItems.length === 0) {
        message.error(t('請至少選擇一個商品進行退貨'));
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      form.validateFields().then(() => {
        setCurrentStep(2);
      }).catch(() => {
        message.error(t('請填寫所有必需的字段'));
      });
    } else if (currentStep === 2) {
      handleConfirmProcessing();
    }
  };

  const handleConfirmProcessing = () => {
    setProcessing(true);
    
    // 模拟處理過程
    setTimeout(() => {
      setProcessing(false);
      setCurrentStep(3);
      onStatusUpdate(
        'completed',
        form.getFieldValue('merchantConfirmation'),
        {
          returnReason: form.getFieldValue('returnReason'),
          handlingMethod: form.getFieldValue('handlingMethod'),
        }
      );
      message.success(t('退貨處理完成'));
    }, 2000);
  };

  // 表格列配置
  const itemColumns: ColumnsType<ProcessingItem> = [
    {
      title: t('選擇'),
      width: 60,
      render: (_, record, index) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleItemChange(index, 'selected', e.target.checked)}
          disabled={!editMode}
        />
      ),
    },
    {
      title: t('商品信息'),
      dataIndex: 'product',
      render: (product: any, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{product.name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('SKU:')} {product.sku} {t('| 原訂購:')} {record.originalQuantity}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('可退貨:')} {record.maxReturnQuantity}
          </div>
        </div>
      ),
    },
    {
      title: t('退貨數量'),
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          min={1}
          max={record.maxReturnQuantity}
          value={record.returnQuantity}
          onChange={(value) => handleItemChange(index, 'returnQuantity', value || 1)}
          disabled={!record.selected || !editMode}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('退貨原因'),
      width: 150,
      render: (_, record, index) => (
        <Select
          value={record.returnReason}
          onChange={(value) => handleItemChange(index, 'returnReason', value)}
          disabled={!record.selected || !editMode}
          style={{ width: '100%' }}
        >
          {returnReasons.map(reason => (
            <Option key={reason.value} value={reason.value}>
              {reason.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: t('商品狀態'),
      width: 120,
      render: (_, record, index) => {
        if (isReadOnly || !editMode || !record.selected) {
          const meta = getConditionMeta(record.condition);
          return <Tag color={meta.color}>{meta.label}</Tag>;
        }
        return (
          <Select
            value={record.condition}
            onChange={(value) => handleItemChange(index, 'condition', value)}
            disabled={!record.selected || !editMode}
            style={{ width: '100%' }}
          >
            {conditions.map(condition => (
              <Option key={condition.value} value={condition.value}>
                <Tag color={condition.color}>{condition.label}</Tag>
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: t('退款金額'),
      width: 100,
      render: (_, record) => (
        <Text strong style={{ color: record.selected ? '#f5222d' : '#666' }}>
          ¥{(record.returnQuantity * record.unitPrice).toFixed(2)}
        </Text>
      ),
    },
  ];

  const steps = [
    {
      title: t('申請退貨'),
      description: t('填寫退貨原因'),
      icon: <InfoCircleOutlined />,
    },
    {
      title: t('商家確認'),
      description: t('聯繫商家確認處理方式'),
      icon: <PhoneOutlined />,
    },
    {
      title: t('完成處理'),
      description: t('確認處理結果完成處理'),
      icon: <CheckCircleOutlined />,
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message={t('退貨處理流程')}
              description={t('請按照步驟完成退貨處理：1) 填寫退貨原因 → 2) 聯繫商家確認處理方式 → 3) 根據確認結果完成處理')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Card 
              title={t('退貨商品清單')} 
              extra={!isReadOnly && (
                <Button 
                  type="primary" 
                  icon={editMode ? <SaveOutlined /> : <EditOutlined />}
                  onClick={() => setEditMode(!editMode)}
                >
                  {editMode ? t('保存') : t('編輯')}
                </Button>
              )}
            >
              <Table
                columns={itemColumns}
                dataSource={returnItems}
                rowKey="originalOrderItemId"
                pagination={false}
                size="small"
                summary={() => {
                  const selectedItems = getSelectedItems();
                  const totalRefund = calculateTotalRefund();
                  
                  return (
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <Text strong>
                          {t('已選擇')} {selectedItems.length} {t('個商品， 退貨數量總計:')} {selectedItems.reduce((sum, item) => sum + item.returnQuantity, 0)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text strong style={{ color: '#f5222d' }}>
                          {t('退款總額: ¥')}{totalRefund.toFixed(2)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>

            <Form form={form} layout="vertical" style={{ marginTop: 16 }} requiredMark={!isReadOnly}>
              <Card title={t('詳細說明')}>
                <Form.Item
                  name="returnReason"
                  label={t('退貨原因')}
                  rules={isReadOnly ? [] : [{ required: true, message: t('請填寫退貨原因') }]}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('請詳細描述退貨原因...')}
                    maxLength={500}
                    showCount
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Form.Item>
              </Card>
            </Form>
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message={t('聯繫商家確認處理方式')}
              description={t('請聯繫商家確認具體的處理方式，並在下方填寫商家的確認內容。')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form form={form} layout="vertical">
              <Card title={t('處理方式確認')}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="handlingMethod"
                      label={t('處理方式')}
                      rules={[{ required: true, message: t('請選擇處理方式') }]}
                    >
                      <Select
                        placeholder={t('選擇處理方式')}
                        dropdownMatchSelectWidth={false}
                        dropdownStyle={{ minWidth: 320 }}
                      >
                        {handlingMethods.map(method => (
                          <Option key={method.value} value={method.value}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <span>{method.label}</span>
                              <span style={{ fontSize: '12px', color: '#666' }}>— {method.description}</span>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title={t('預計退款金額')}
                      value={calculateTotalRefund()}
                      prefix="¥"
                      precision={2}
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Col>
                </Row>
                
                <Form.Item
                  name="merchantConfirmation"
                  label={t('商家確認處理方式')}
                  rules={[{ required: true, message: t('請填寫商家確認的處理方式') }]}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('請填寫商家確認的處理方式和備註...')}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
              </Card>
            </Form>
          </div>
        );

      case 2:
        const selectedItems = getSelectedItems();
        const formValues = form.getFieldsValue();
        
        return (
          <div>
            <Alert
              message={t('確認處理結果')}
              description={t('請確認以下處理信息，確認無誤後點擊完成處理。')}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Card title={t('處理結果確認')}>
              <Descriptions bordered column={2}>
                <Descriptions.Item label={t('訂單號')}>{order.orderNumber}</Descriptions.Item>
                <Descriptions.Item label={t('客戶')}>{order.customer.name}</Descriptions.Item>
                <Descriptions.Item label={t('退貨商品數')}>{selectedItems.length}</Descriptions.Item>
                <Descriptions.Item label={t('退款金額')}>
                  <Text style={{ fontSize: '16px', color: '#f5222d', fontWeight: 'bold' }}>
                    ¥{calculateTotalRefund().toFixed(2)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('處理方式')} span={2}>
                  {handlingMethods.find(m => m.value === formValues.handlingMethod)?.label}
                </Descriptions.Item>
                <Descriptions.Item label={t('商家確認')} span={2}>
                  {formValues.merchantConfirmation}
                </Descriptions.Item>
              </Descriptions>

              <Divider>{t('退貨商品明細')}</Divider>
              
              {selectedItems.map(item => (
                <div key={item.originalOrderItemId} style={{ 
                  padding: '12px', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: '6px', 
                  marginBottom: '8px' 
                }}>
                  <Row>
                    <Col span={12}>
                      <Text strong>{item.product.name}</Text>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {t('SKU:')} {item.product.sku}
                      </div>
                    </Col>
                    <Col span={6}>
                      <Text>{t('退貨數量:')} {item.returnQuantity}</Text>
                    </Col>
                    <Col span={6}>
                      <Text>{t('退款金額: ¥')}{(item.returnQuantity * item.unitPrice).toFixed(2)}</Text>
                    </Col>
                  </Row>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    {t('退貨原因:')} {returnReasons.find(r => r.value === item.returnReason)?.label} {t('| 商品狀態:')} {conditions.find(c => c.value === item.condition)?.label}
                  </div>
                </div>
              ))}
            </Card>
          </div>
        );

      case 3:
        return (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={2}>{t('退貨處理完成！')}</Title>
              <Paragraph>
                {t('退貨申請已成功處理，退款將在1-3個工作日內到賬。')}
                                      </Paragraph>
              <Statistic
                title={t('退款金額')}
                value={calculateTotalRefund()}
                prefix="¥"
                precision={2}
                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
              />
              <div style={{ marginTop: '24px' }}>
                <Button type="primary" size="large" onClick={onComplete}>
                  {t('完成')}
                                            </Button>
              </div>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '16px' }}>
      {/* 訂單信息頭部 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={6}>
            <Statistic title={t('訂單號')} value={order.orderNumber} />
          </Col>
          <Col span={6}>
            <Statistic title={t('客戶')} value={order.customer.name} />
          </Col>
          <Col span={6}>
            <Statistic title={t('當前狀態')} value="已收貨" />
          </Col>
          <Col span={6}>
            <Statistic 
              title={t('訂單金額')} 
              value={order.totalAmount} 
              prefix="¥" 
              precision={2}
            />
          </Col>
        </Row>
      </Card>

      {/* 步驟条 */}
      <Card style={{ marginBottom: 16 }}>
        <Steps current={currentStep} items={steps} />
      </Card>

      {/* 步驟內容 */}
      {renderStepContent()}

      {/* 操作按钮 */}
      {!isReadOnly && currentStep < 3 && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <Space>
              {currentStep > 0 && (
                <Button onClick={() => setCurrentStep(currentStep - 1)}>
                  {t('上一步')}
                                                  </Button>
              )}
              <Button 
                type="primary" 
                onClick={handleNextStep}
                loading={processing}
              >
                {currentStep === 2 ? t('完成處理') : t('下一步')}
              </Button>
            </Space>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ReturnOrderProcessing;
